import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { PartialDeep } from 'type-fest';
import { mergeDeep } from '@pcmp-checka/lib-utilities/object';
import { LocalStorageAdapter } from '../../../lib/adapters/local-storage';

export const adapter = new LocalStorageAdapter('shopping-bag');

export type UpdateItemAction = PayloadAction<{
  bag: 'shoppingItems' | 'wardrobeItems';
  id: string;
  amount: number;
}>;

export type SetValueAction = PayloadAction<{
  key: keyof AppConfigState;
  value: unknown;
}>;

export type UpdateShoppingBagAction = PayloadAction<
  PartialDeep<AppConfigState>
>;

type BagItem = {
  id: string;
  amount: number;
};

// Define a type for the slice state
type AppConfigState = {
  location: string;
  onBehalfOf: {
    email: string;
    displayName: string;
    manager: string;
  } | null;
  shoppingItems: BagItem[];
  wardrobeItems: BagItem[];
};

// Define the initial state using that type
const defaultInitialState = {
  location: '',
  onBehalfOf: null,
  shoppingItems: [],
  wardrobeItems: [],
};

const getInitialState = () => {
  let initial: AppConfigState = { ...defaultInitialState };

  try {
    const keys = Object.keys(initial) as (keyof AppConfigState)[];

    keys.forEach((key) => {
      const value = adapter.getItem(key);

      if (value !== null) {
        initial = { ...initial, [key]: value };
      }
    });
  } catch {}

  return initial;
};

export const shoppingBagSlice = createSlice({
  name: 'shoppingBag',

  initialState: getInitialState(),

  /**
   * @internal These reducers are prefixed with _ to indicate they should not be
   * imported directly. Use the hooks from `components/page-widgets/webshop/context.tsx`
   * instead (useUpdateItem, useUpdateBag, useResetBag).
   * @see docs/app/webshop-context.md
   */
  reducers: {
    _updateItem: (state, action: UpdateItemAction) => {
      const { bag, id, amount } = action.payload;

      state[bag] ||= [];

      if (amount <= 0) {
        state[bag] = state[bag].filter((item) => item.id !== id);
        return;
      }

      const existingItem = state[bag].find((item) => item.id === id);

      if (existingItem) {
        existingItem.amount = amount;
      } else {
        state[bag].push({
          id,
          amount,
        });
      }
    },

    _updateShoppingBag(state, action: UpdateShoppingBagAction) {
      state = mergeDeep(state, action.payload);
    },

    setValue(state, action: SetValueAction) {
      const { key, value } = action.payload;
      // @ts-expect-error not strictly typed
      state[key] = value;
    },

    _resetShoppingBag(state: AppConfigState) {
      // Preserve location on reset, as it's not directly related to the bag contents
      // and can be inconvenient for users to reselect after every order.
      return { ...defaultInitialState, location: state.location };
    },
  },
});

export const { _updateItem, setValue, _updateShoppingBag, _resetShoppingBag } =
  shoppingBagSlice.actions;

export default shoppingBagSlice.reducer;
